import { ExpandLess, ExpandMore, Menu as MenuIcon } from "@mui/icons-material";
import {
    AppBar,
    Box,
    Button,
    Collapse,
    CssBaseline,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Drawer,
    List,
    ListItemButton,
    ListItemText,
    TextField,
    Toolbar,
    Typography,
    LinearProgress,
    Avatar,
    Chip,
    Divider,
    IconButton,
    Tooltip,
    Fade,
    ListItemIcon,
    Badge,
    Switch,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {
    Dashboard,
    Assignment,
    VideoLibrary,
    FolderShared,
    Announcement,
    School,
    Quiz,
    Visibility,
    Logout,
    AccountCircle,
    MenuBook,
    TrendingUp,
    EmojiEvents,
    Event as EventIcon,
    CollectionsBookmark,
    FactCheck,
    Article,
    AutoAwesome,
    Inbox,
    MailOutline,
    Lock,
} from "@mui/icons-material";
import { useState, useEffect, useRef, useMemo } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import logout from "../../logout";
import CapitalizeFirstLetter from "../../CapitalizeFirstLetter";
import api from "../../api";
import { getPredefinedAvatarImageUrl } from "../../utils/studentAvatarOptions";
import { PortalToggleSwitch } from "../../components";
import TesterPortalSwitcher from "../../components/TesterPortalSwitcher";
import ParentWelcomeTour from "../../components/ParentWelcomeTour";
import AssistantDrawer from "../../components/PortalAssistant/AssistantDrawer";
import useExamMode from "../../hooks/useExamMode";
import usePracticeModeVisible from "../../hooks/usePracticeModeVisible";
import useStudentPortalMode, { setDashboardView } from "../../hooks/useStudentPortalMode";
import DashboardViewToggle from "../Student/components/DashboardViewToggle";
import { getStudentPermissionsFromStorage } from "../../utils/studentPermissions";
import {
    studentIsAllowMobile,
    patchStudentUserData,
} from "../../utils/studentMobileAccess";
import { navIconTileSx, navIconTileHoverSx } from "../../components/navIconTile";

const drawerWidth = 280;
const MINI_DRAWER_WIDTH = 64;
const PRACTICE_MODE_TOOLTIP =
    "Practice mode applies to mock exams, practice papers, and worksheets. Testers start with Practice on; others start off after login. Turn it on to use Check answer. Attempts are not recorded while this is on.";

/**
 * What a student with an ended plan may still open.
 *
 * The API deliberately leaves mock exams, past papers and resource-checkout outside
 * `EnsureStudentSubscriptionAccess` so the free tier works and a lapsed student can buy
 * individual items — which is the only way back in for someone with nothing bought.
 * Redirecting every route to the plan screen contradicted that and left them stranded.
 *
 * Path segments, matched against the part after `/${name}/`. An empty string is the
 * dashboard itself, which stays reachable so the plan offer has somewhere to live.
 */
const LAPSED_PLAN_ALLOWED_SEGMENTS = [
    "",
    "choose-plan",
    "mock-exams",
    "mock-exam",
    "past-papers",
    "past-paper",
    "profile",
    "settings",
    "notifications",
];

/**
 * Is this path one a lapsed-plan student may still open?
 *
 * After an ended free trial (`catalogueLocked`) only the plan screen, profile and settings
 * stay open. A lapsed paid plan can still open mock exams and practice papers.
 */
const ENDED_TRIAL_ALLOWED_SEGMENTS = ["choose-plan", "profile", "settings"];

const isLapsedPlanAllowedPath = (pathname, prefix, { catalogueLocked = false, extrasOpen = false, wizardOpen = false } = {}) => {
    const rest = pathname
        .replace(/\/+$/, "")
        .replace(new RegExp(`^/${prefix}`), "")
        .replace(/^\//, "");
    const segment = rest.split("/")[0] ?? "";

    if (catalogueLocked) {
        return ENDED_TRIAL_ALLOWED_SEGMENTS.includes(segment);
    }
    // A buyer keeps flash cards, games and the wizard after a paid plan ends.
    if (extrasOpen && EXTRAS_SEGMENTS.includes(segment)) {
        return true;
    }
    if (wizardOpen && segment === "wizard") {
        return true;
    }

    return LAPSED_PLAN_ALLOWED_SEGMENTS.includes(segment);
};

/** Flash cards (and their vocabulary view) and games: see extrasLockedFor() on the API. */
const EXTRAS_SEGMENTS = ["flash-cards", "vocabulary", "games"];

const drawerPaperSx = (width, appBarHeight, shadow) => ({
    width,
    boxSizing: "border-box",
    mt: `${appBarHeight}px`,
    // dvh so iOS Safari's bottom toolbar doesn't cover the end of the menu.
    maxHeight: `calc(100vh - ${appBarHeight}px)`,
    "@supports (height: 100dvh)": { maxHeight: `calc(100dvh - ${appBarHeight}px)` },
    background: "linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%)",
    borderRight: "none",
    boxShadow: shadow,
    overflowY: "auto",
    overflowX: "hidden",
    transition: "width 0.3s ease",
    "&::-webkit-scrollbar": { width: "8px" },
    "&::-webkit-scrollbar-track": { background: "rgba(0,0,0,0.05)", borderRadius: "10px" },
    "&::-webkit-scrollbar-thumb": {
        background: "linear-gradient(180deg, #667eea 0%, #764ba2 100%)",
        borderRadius: "10px",
        border: "1px solid rgba(255,255,255,0.2)",
    },
    "&::-webkit-scrollbar-thumb:hover": {
        background: "linear-gradient(180deg, #3B2A9F 0%, #D62926 100%)",
    },
    scrollbarWidth: "thin",
    scrollbarColor: "#667eea rgba(0,0,0,0.05)",
});

const StudentLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isCompactAppBar = useMediaQuery(theme.breakpoints.down("sm"));
    const appBarHeight = isCompactAppBar ? 56 : 70;
    const [openMenus, setOpenMenus] = useState({});
    const loading = false;
    const [userData, setUserData] = useState(null); // user data from localStorage
    const [announcementCount, setAnnouncementCount] = useState(0); // announcement count for badge
    const [notificationCount, setNotificationCount] = useState(0);
    const [messageCount, setMessageCount] = useState(0);
    // Sidebar RBAC. Seeded from localStorage so the menu paints before the sync lands, then
    // replaced by the authoritative list for whichever account this token actually belongs to.
    const [studentPermissions, setStudentPermissions] = useState(() =>
        getStudentPermissionsFromStorage()
    );
    const {
        showDashboard,
        hideCourseOnlySections,
        hideMobileOnlySections,
        subscriptionLock,
        planFullyLocked,
        catalogueLocked,
        extrasLocked,
        wizardLocked,
        canToggleDashboard,
        dashboardView,
    } = useStudentPortalMode();
    // A lapsed plan or ended free trial hides Work Sheet, Flash Cards, Games and Wizard unless
    // the student paid for a course, mock exam, past or practice paper, or pack — then Flash
    // Cards, Games and Wizard stay (Work Sheet only after a paid plan). Same rules the API
    // enforces in MobileSubscriptionAccessService, and routes/api/student.php uses.
    const planLocked = subscriptionLock?.locked === true;
    const worksheetsLocked = planLocked && subscriptionLock?.worksheetsLocked !== false;
    // Which lapsed-plan pages still open: see isLapsedPlanAllowedPath().
    const lapsedAccess = { catalogueLocked, extrasOpen: !extrasLocked, wizardOpen: !wizardLocked };
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [mobileFull, setMobileFull] = useState(false);
    const [pinDialogOpen, setPinDialogOpen] = useState(false);
    const [switchPin, setSwitchPin] = useState("");
    const [switchPinError, setSwitchPinError] = useState("");
    const [switchPinLoading, setSwitchPinLoading] = useState(false);
    const [parentSwitchRedirect, setParentSwitchRedirect] = useState(null);
    const [forgotPinLoading, setForgotPinLoading] = useState(false);
    const pinInputRefs = useRef([null, null, null, null]);
    const name = process.env.REACT_APP_STUDENT_PREFIX; //getting the prefix from the environment variable
    // A lapsed plan hides the plan-only sections, but the catalogues stay open so the
    // student can buy a single mock exam or past paper without renewing first.
    const onLapsedAllowedPage = isLapsedPlanAllowedPath(location.pathname, name, lapsedAccess);
    const { mockExamsMode, setMode, settingMode } = useExamMode();
    const practiceModeVisible = usePracticeModeVisible();

    useEffect(() => {
        if (!isMobile) {
            setSidebarOpen(true);
            setMobileFull(false);
        }
    }, [isMobile]);

    useEffect(() => {
        if (isMobile) setMobileFull(false);
    }, [location.pathname, isMobile]);

    const handleHamburger = () => {
        if (isMobile) {
            setMobileFull((prev) => !prev);
        } else {
            setSidebarOpen((prev) => !prev);
        }
    };

    const handleOpenParentPinDialog = (redirectTo = null) => {
        setParentSwitchRedirect(typeof redirectTo === "string" ? redirectTo : null);
        setSwitchPin("");
        setSwitchPinError("");
        setPinDialogOpen(true);
    };

    const handleCloseParentPinDialog = () => {
        if (!switchPinLoading) {
            setPinDialogOpen(false);
            setSwitchPin("");
            setSwitchPinError("");
            setParentSwitchRedirect(null);
        }
    };

    useEffect(() => {
        if (pinDialogOpen) {
            setSwitchPin("");
            const t = setTimeout(() => pinInputRefs.current[0]?.focus(), 100);
            return () => clearTimeout(t);
        }
    }, [pinDialogOpen]);

    const handleSwitchToParent = async () => {
        const pin = String(switchPin).trim();
        if (pin.length !== 4 || !/^\d{4}$/.test(pin)) {
            setSwitchPinError("Enter a 4-digit PIN.");
            return;
        }
        setSwitchPinError("");
        setSwitchPinLoading(true);
        try {
            const res = await api.post("student/switch-to-parent", { pin });
            const data = res.data?.data;
            if (data?.access_token) {
                // Save current student session so parent can switch back without PIN
                try {
                    const studentSession = {
                        token: localStorage.getItem("token"),
                        role: localStorage.getItem("role"),
                        userData: localStorage.getItem("userData"),
                        mustChangePassword: localStorage.getItem("mustChangePassword"),
                        // Without this the switch back restores a session with no student-permissions,
                        // and every permission check falls open — course-only tabs appear for mobile students.
                        studentPermissions: localStorage.getItem("student-permissions"),
                    };
                    sessionStorage.setItem("studentSessionForSwitch", JSON.stringify(studentSession));
                } catch (e) { /* ignore */ }
                localStorage.setItem("token", data.access_token);
                localStorage.setItem("role", (data.role || "parent").toString().toLowerCase());
                localStorage.setItem("userData", JSON.stringify(data));
                if (data.must_change_password !== undefined) {
                  localStorage.setItem("mustChangePassword", data.must_change_password ? "1" : "0");
                }
                if (Array.isArray(data.permissions)) {
                  localStorage.setItem("parent-permissions", JSON.stringify(data.permissions));
                }
                // The emailed sign-up (or reset) PIN works once: Settings asks for their own.
                if (data.must_change_parent_pin) {
                  localStorage.setItem("mustChangeParentPin", "1");
                } else {
                  localStorage.removeItem("mustChangeParentPin");
                }
                // The session is the parent's now; leaving the student's permissions
                // behind lets the student portal keep rendering against a parent token.
                // The copy saved in sessionStorage above is what the switch back restores.
                localStorage.removeItem("student-permissions");
                toast.success("Switched to parent portal.");
                setPinDialogOpen(false);
                setSwitchPin("");
                const redirectTo = data.must_change_password || data.must_change_parent_pin
                    ? "/parent/settings"
                    : (parentSwitchRedirect || "/parent");
                setParentSwitchRedirect(null);
                window.location.assign(redirectTo);
            } else {
                setSwitchPinError("Invalid response from server.");
            }
        } catch (err) {
            const data = err.response?.data;
            if (err.response?.status === 401) {
                setSwitchPinError("Incorrect PIN. Please try again.");
            } else {
                const msg = data?.data?.error ?? data?.message ?? "Invalid PIN or unable to switch.";
                setSwitchPinError(msg);
            }
        } finally {
            setSwitchPinLoading(false);
        }
    };

    const handleForgotPin = async () => {
        setForgotPinLoading(true);
        try {
            const response = await api.post("student/forgot-parent-pin");
            if (response.data?.success) {
                toast.success(response.data.message || "A new PIN has been sent to your email.");
            } else {
                toast.error("Failed to reset PIN. Please contact support.");
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Error resetting PIN. Please try again later.");
        } finally {
            setForgotPinLoading(false);
        }
    };

    const handleLogout = () => {
        logout();
        window.location.href = "/login";
    };//logout

    const handlePracticeToggle = async (event) => {
        const nextMode = event.target.checked ? "testing" : "actual";
        try {
            await setMode(nextMode);
            toast.success(nextMode === "testing" ? "Practice mode enabled." : "Actual mode enabled.");
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to change exam mode.");
        }
    };

    const menuItems = useMemo(() => {
        const items = [
        {
            label: "Dashboard",
            icon: Dashboard,
            path: `/${name}`,
            color: "#2196f3",
            gradient: "linear-gradient(135deg, #2196f3 0%, #1976d2 100%)",
            permission: "student-portal-view-dashboard",
            alwaysVisible: showDashboard,
        },
        {
            label: "Homework",
            icon: MenuBook,
            courseOnly: true,
            color: "#4caf50",
            gradient: "linear-gradient(135deg, #4caf50 0%, #388e3c 100%)",
            children: [
                {
                    label: "My Course Assignment",
                    icon: Assignment,
                    path: `/${name}/my-current-assignment`,
                    color: "#4caf50",
                    permission: "student-portal-homework-my-assignment",
                },
                {
                    label: "My Assignment Results",
                    icon: Quiz,
                    path: `/${name}/my-course-test`,
                    color: "#ff9800",
                    permission: "student-portal-homework-assignment-results",
                },
                {
                    // Tutor-uploaded PDFs/PPTs for the group. Homework, not a section of its own —
                    // the same files also appear inline per week on My Course Assignment.
                    label: "Class Resources",
                    icon: FolderShared,
                    path: `/${name}/class-resources`,
                    color: "#00897b",
                    permission: "student-portal-classes",
                    courseOnly: true,
                },
            ],
        },
        {
            label: "Video",
            icon: VideoLibrary,
            courseOnly: true,
            color: "#9c27b0",
            gradient: "linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)",
            children: [
                {
                    label: "My Course Video",
                    icon: VideoLibrary,
                    path: `/${name}/my-course-video`,
                    color: "#9c27b0",
                    permission: "student-portal-video-my-course-video",
                },
                {
                    label: "My Video Views",
                    icon: Visibility,
                    path: `/${name}/my-course-view`,
                    color: "#e91e63",
                    permission: "student-portal-video-my-video-views",
                },
                {
                    // Recordings of the group's live classes. Video, not a section of its own.
                    label: "Class Recordings",
                    icon: VideoLibrary,
                    path: `/${name}/class-recordings`,
                    color: "#5c6bc0",
                    permission: "student-portal-classes",
                    courseOnly: true,
                },
            ],
        },
        {
            label: "Work Sheet",
            icon: FactCheck,
            path: `/${name}/ai-answer-sheet`,
            color: "#673ab7",
            gradient: "linear-gradient(135deg, #7e57c2 0%, #5e35b1 100%)",
            permission: "student-portal-worksheet",
            worksheet: true,
        },
        {
            label: "Mock Exams",
            icon: Quiz,
            path: `/${name}/mock-exams`,
            color: "#d32f2f",
            gradient: "linear-gradient(135deg, #ef5350 0%, #c62828 100%)",
            permission: "student-portal-mock-exams",
        },
        {
            label: "Practice Papers",
            icon: Article,
            path: `/${name}/past-papers`,
            color: "#1565c0",
            gradient: "linear-gradient(135deg, #42a5f5 0%, #0d47a1 100%)",
            anyOf: ["student-portal-past-papers", "student-portal-mock-exams"],
        },
        {
            label: "Flash Cards",
            icon: CollectionsBookmark,
            path: `/${name}/flash-cards`,
            color: "#009688",
            gradient: "linear-gradient(135deg, #009688 0%, #00796b 100%)",
            // Kept for anyone who paid for a course, mock exam, past or practice paper, or
            // pack — after a lapsed plan or an ended free trial (extrasLockedFor on the API).
            extras: true,
            children: [
                {
                    label: "Flash Cards",
                    icon: CollectionsBookmark,
                    path: `/${name}/flash-cards`,
                    color: "#009688",
                    permission: "student-portal-flash-cards",
                },
                {
                    label: "Quiz Style Flash Cards",
                    icon: CollectionsBookmark,
                    path: `/${name}/flash-cards?mode=quiz`,
                    color: "#6366f1",
                    permission: "student-portal-flash-cards-quiz",
                },
            ],
        },
        {
            label: "Classes",
            icon: EventIcon,
            courseOnly: true,
            path: `/${name}/classes`,
            color: "#00acc1",
            gradient: "linear-gradient(135deg, #00acc1 0%, #00838f 100%)",
            permission: "student-portal-classes",
        },
        // Class Resources and Class Recordings are deliberately not top-level: they live under
        // Homework and Video respectively, where students already look for that material.
        {
            label: "Games",
            icon: Quiz,
            color: "#e91e63",
            gradient: "linear-gradient(135deg, #e91e63 0%, #c2185b 100%)",
            extras: true,
            children: [
                {
                    label: "Play Games",
                    icon: Quiz,
                    path: `/${name}/games`,
                    color: "#e91e63",
                    permission: "student-portal-games-play",
                },
                {
                    label: "Leaderboard",
                    icon: EmojiEvents,
                    path: `/${name}/games/leaderboard`,
                    color: "#ff9800",
                    permission: "student-portal-games-leaderboard",
                },
            ],
        },
        {
            label: "Notifications",
            icon: Inbox,
            path: `/${name}/notifications`,
            color: "#5c6bc0",
            gradient: "linear-gradient(135deg, #5c6bc0 0%, #3949ab 100%)",
        },
        {
            // Copies of the emails we sent this student.
            label: "Messages",
            icon: MailOutline,
            path: `/${name}/messages`,
            color: "#5c6bc0",
            gradient: "linear-gradient(135deg, #5c6bc0 0%, #3949ab 100%)",
        },
        {
            label: "Announcements",
            icon: Announcement,
            courseOnly: true,
            path: `/${name}/announcements`,
            color: "#ff5722",
            gradient: "linear-gradient(135deg, #ff5722 0%, #d84315 100%)",
            permission: "student-portal-announcements",
        },
        {
            label: "Certificates",
            icon: EmojiEvents,
            path: `/${name}/certificates`,
            color: "#d4af37",
            gradient: "linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)",
            permission: "student-portal-certificates",
        },
        ];

        // Wizard matches the mobile app tab — only for is_allow_mobile students.
        // Wizard belongs to the mobile plan: not on the Course side of a course + plan student.
        if (studentIsAllowMobile(userData) && !(canToggleDashboard && dashboardView === "course")) {
            const worksheetIdx = items.findIndex((i) => i.label === "Work Sheet");
            const insertAt = worksheetIdx >= 0 ? worksheetIdx + 1 : 3;
            items.splice(insertAt, 0, {
                label: "Wizard",
                icon: AutoAwesome,
                path: `/${name}/wizard`,
                color: "#4f46e5",
                gradient: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                planOnly: true,
            });
        }

        // Plan ended: one way back to a plan, alongside the sections that stay open. With
        // nothing bought every tab is locked anyway, and the page below is already the plan.
        // An ended trial keeps the link too: it may still open flash cards, games and wizard.
        if (planLocked && (!planFullyLocked || catalogueLocked)) {
            const pastPapersIdx = items.findIndex((i) => i.label === "Practice Papers");
            items.splice(pastPapersIdx >= 0 ? pastPapersIdx + 1 : items.length, 0, {
                label: "Mobile Plan",
                icon: AutoAwesome,
                path: `/${name}/choose-plan?plans=1`,
                color: "#667eea",
                gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            });
        }

        return items;
    }, [name, userData, showDashboard, planLocked, planFullyLocked, catalogueLocked, canToggleDashboard, dashboardView]);

    /** Portal-shape gate (course vs mobile student), independent of RBAC. */
    const allowSectionMode = (entry) => {
        if (entry.courseOnly && hideCourseOnlySections) {
            return false;
        }
        if (entry.mobileOnly && hideMobileOnlySections) {
            return false;
        }
        if (entry.planOnly && wizardLocked) {
            return false;
        }
        if (entry.worksheet && worksheetsLocked) {
            return false;
        }
        if (entry.extras && extrasLocked) {
            return false;
        }
        return true;
    };

    // `null` means no list was ever synced (legacy session) — those checks fall open.
    const hasPermission = (permission) =>
        studentPermissions === null ? true : studentPermissions.includes(permission);
    const hasAnyPermission = (permissions) =>
        studentPermissions === null ? true : permissions.some((p) => studentPermissions.includes(p));

    const allowMenuItem = (entry) => {
        if (!allowSectionMode(entry)) {
            return false;
        }
        if (entry.alwaysVisible) {
            return true;
        }
        if (Array.isArray(entry.anyOf) && entry.anyOf.length > 0) {
            return hasAnyPermission(entry.anyOf);
        }
        if (entry.permission) {
            return hasPermission(entry.permission);
        }
        return true;
    };

    const visibleMenuItems = menuItems
        .map((item) => {
            if (item.children) {
                // A parent's own courseOnly/mobileOnly flag still applies — otherwise Homework and
                // Video reappear for mobile students whenever their children pass on permissions.
                if (!allowSectionMode(item)) {
                    return null;
                }
                const children = item.children.filter(allowMenuItem);
                return children.length ? { ...item, children } : null;
            }
            return allowMenuItem(item) ? item : null;
        })
        .filter(Boolean);

    // The SSO handoff seeds `student-permissions` from whatever the other app held, which can be
    // stale or belong to a different account. Re-read it from the API for this token, the way the
    // admin, parent and tutor layouts already do, so the sidebar never runs on a foreign list.
    useEffect(() => {
        let cancelled = false;
        api.get("student/auth/permissions", {
            validateStatus: (status) => status === 200 || status === 404,
        })
            .then((res) => {
                if (cancelled) return;
                // Treat 404 as non-fatal: older APIs omit the endpoint, so keep the seeded list.
                if (res.status === 404) return;
                const perms = res.data?.data?.permissions;
                if (!res.data?.success || !Array.isArray(perms)) return;
                localStorage.setItem("student-permissions", JSON.stringify(perms));
                setStudentPermissions(perms);
            })
            .catch(() => {
                /* keep the seeded list rather than blanking the sidebar on a transient failure */
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const handleToggle = (label) => {
        setOpenMenus((prev) => ({
            ...prev,
            [label]: !prev[label],
        }));
    };

    // Load user data from localStorage (re-read when location changes so profile updates show)
    useEffect(() => {
        const storedUserData = localStorage.getItem('userData');
        if (storedUserData) {
            try {
                const parsedData = JSON.parse(storedUserData);
                setUserData(parsedData);
            } catch (error) {
                console.error('Error parsing user data from localStorage:', error);
            }
        }
    }, [location.pathname]);

    // Keep is_allow_mobile fresh so Wizard appears for mobile-registered students.
    useEffect(() => {
        let cancelled = false;
        api.get("student/profile")
            .then((res) => {
                if (cancelled) return;
                const allow = Boolean(res.data?.data?.is_allow_mobile);
                setUserData((prev) => {
                    const next = patchStudentUserData({
                        ...(prev || {}),
                        is_allow_mobile: allow,
                    });
                    return next;
                });
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);

    // Unread emails for the Messages badge; fetched on its own so a failure leaves the others.
    const fetchMessageCount = () => {
        api.get('student/messages/unread-count')
            .then((r) => setMessageCount(Number(r.data?.data?.unread_count) || 0))
            .catch(() => setMessageCount(0));
    };
    useEffect(() => {
        window.addEventListener('panel-messages-read', fetchMessageCount);
        return () => window.removeEventListener('panel-messages-read', fetchMessageCount);
    }, []);

    // Fetch unread announcement/notification count for badge (only show when user has unviewed)
    const fetchUnreadCount = async () => {
        fetchMessageCount();
        try {
            const [announcements, unread] = await Promise.all([
                api.get('student/notifications/unread-announcement-count'),
                api.get('student/notifications/unread-count'),
            ]);
            const announcementTotal = announcements.data?.success && announcements.data?.data?.count != null
                ? Number(announcements.data.data.count) || 0
                : 0;
            const unreadTotal = unread.data?.success && unread.data?.data?.count != null
                ? Number(unread.data.data.count) || 0
                : 0;
            setAnnouncementCount(announcementTotal);
            setNotificationCount(Math.max(0, unreadTotal - announcementTotal));
        } catch (error) {
            console.error('Error fetching unread count:', error);
            setAnnouncementCount(0);
            setNotificationCount(0);
        }
    };

    // When user opens Announcements page, mark all announcement notifications as read so badge shows 0
    useEffect(() => {
        if (location.pathname.startsWith(`/${name}/announcements`)) {
            api.post('student/notifications/mark-all-announcements-read').then(() => {
                setAnnouncementCount(0);
                fetchUnreadCount();
            }).catch(() => {});
        } else {
            fetchUnreadCount();
        }
    }, [location.pathname, name]);

    // Auto-open parent menus based on current route and simulate page load
    useEffect(() => {
        // Auto-open parent menus if child route is active
        const currentPath = location.pathname;
        const newOpenMenus = {};

        menuItems.forEach(item => {
            if (item.children) {
                const hasActiveChild = item.children.some(child => child.path === currentPath);
                if (hasActiveChild) {
                    newOpenMenus[item.label] = true;
                }
            }
        });

        // Only update if there are changes to avoid unnecessary re-renders
        if (Object.keys(newOpenMenus).length > 0) {
            setOpenMenus(prev => ({ ...prev, ...newOpenMenus }));
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]); // reset when route changes

    const desktopDrawerWidth = sidebarOpen ? drawerWidth : MINI_DRAWER_WIDTH;

    const renderSidebarContent = (minified) => (
        <>
            <Tooltip title="View student profile" arrow>
                <Box
                    onClick={() => {
                        navigate(`/${name}/profile`);
                        if (isMobile) setMobileFull(false);
                    }}
                    sx={{
                        p: minified ? 1.5 : 3,
                        textAlign: "center",
                        cursor: "pointer",
                        borderRadius: 2,
                        "&:hover": { bgcolor: "rgba(102, 126, 234, 0.08)" },
                    }}
                >
                    <Avatar
                        src={userData?.profile_image_url || getPredefinedAvatarImageUrl(userData?.profile_image)}
                        sx={{
                            width: minified ? 40 : 60,
                            height: minified ? 40 : 60,
                            mx: "auto",
                            mb: minified ? 0 : 2,
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            boxShadow: "0 8px 25px rgba(102, 126, 234, 0.3)",
                            fontSize: minified ? "16px" : "24px",
                            fontWeight: 700,
                        }}
                    >
                        {!userData?.profile_image_url && !getPredefinedAvatarImageUrl(userData?.profile_image) && (
                            userData?.full_name ? userData.full_name.charAt(0).toUpperCase() : <School sx={{ fontSize: minified ? 22 : 30, color: "white" }} />
                        )}
                    </Avatar>
                    {!minified && userData ? (
                        <>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: "#111827", mb: 0.5, fontSize: "16px" }}>
                                {userData.full_name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#374151", mb: 1, display: "block", fontSize: "12px", fontWeight: 700 }}>
                                {userData.email}
                            </Typography>
                            <Chip
                                label={userData.role || "Student"}
                                size="small"
                                sx={{
                                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                    color: "white",
                                    fontWeight: 600,
                                    fontSize: "11px",
                                }}
                            />
                        </>
                    ) : !minified ? (
                        <>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: "#111827", mb: 0.5 }}>
                                Student Panel
                            </Typography>
                            <Chip
                                label="Learning Hub"
                                size="small"
                                sx={{
                                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                    color: "white",
                                    fontWeight: 600,
                                }}
                            />
                        </>
                    ) : null}
                </Box>
            </Tooltip>

            <Divider sx={{ mx: minified ? 1 : 2, mb: 2 }} />

            {!minified && isCompactAppBar && practiceModeVisible && (
                <Tooltip title={PRACTICE_MODE_TOOLTIP} arrow>
                    <Box sx={{
                        mx: 2,
                        mb: 2,
                        px: 2,
                        py: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderRadius: "12px",
                        background: "rgba(255,255,255,0.7)",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}>
                        <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#111827" }}>
                            Practice mode
                        </Typography>
                        <Switch
                            size="small"
                            checked={mockExamsMode === "testing"}
                            onChange={handlePracticeToggle}
                            disabled={settingMode}
                        />
                    </Box>
                </Tooltip>
            )}

            <List sx={{ px: minified ? 0.5 : 2 }}>
                {visibleMenuItems.map((item, index) => {
                    const ItemIcon = item.icon;
                    const pastPapersListPath = `/${name}/past-papers`;
                    const mockExamsListPath = `/${name}/mock-exams`;
                    const isPastPapersNav = item.path === pastPapersListPath;
                    const isMockExamsNav = item.path === mockExamsListPath;
                    const isSelected =
                        location.pathname === item.path?.split("?")[0] ||
                        (isPastPapersNav && location.pathname.startsWith(`/${name}/past-paper/`)) ||
                        (isMockExamsNav && location.pathname.startsWith(`/${name}/mock-exam/`));
                    const isParentSelected = item.children?.some((child) => location.pathname === child.path);

                    const sendToPlan = () => {
                        navigate(`/${name}/choose-plan?plans=1`);
                        if (isMobile) setMobileFull(false);
                        toast.info("Subscribe to a Mobile Plan to unlock this.");
                    };

                    // Mirrors the route guard below. After an ended trial only the plan
                    // screen, profile and settings stay clickable. A lapsed paid plan can
                    // still open mock exams and practice papers.
                    const gateFor = (path) =>
                        planFullyLocked && !isLapsedPlanAllowedPath(path ?? "", name, lapsedAccess);

                    const parentClick = () => {
                        if (gateFor(item.children?.[0]?.path)) {
                            sendToPlan();
                            return;
                        }
                        if (minified && item.children?.[0]?.path) {
                            navigate(item.children[0].path);
                            if (isMobile) setMobileFull(false);
                        } else {
                            handleToggle(item.label);
                        }
                    };

                    const leafClick = (path) => {
                        if (gateFor(path)) {
                            sendToPlan();
                            return;
                        }
                        navigate(path);
                        if (isMobile) setMobileFull(false);
                    };

                    // Padlock/dimming follows the same rule as the click.
                    const itemGated = gateFor(item.path ?? item.children?.[0]?.path);

                    return (
                        <Fade in timeout={300 + index * 100} key={item.label}>
                            <Box sx={{ mb: 1 }}>
                                {item.children ? (
                                    <>
                                        <Tooltip
                                            title={minified ? item.label : ""}
                                            placement="right"
                                            arrow
                                        >
                                            <ListItemButton
                                                data-tour={item.label}
                                                onClick={parentClick}
                                                sx={{
                                                    borderRadius: "12px",
                                                    mb: 1,
                                                    justifyContent: minified ? "center" : "flex-start",
                                                    px: minified ? 1 : 2,
                                                    background: isParentSelected || openMenus[item.label]
                                                        ? item.gradient
                                                        : "rgba(255,255,255,0.7)",
                                                    color: isParentSelected || openMenus[item.label] ? "white" : "#111827",
                                                    boxShadow: isParentSelected || openMenus[item.label]
                                                        ? "0 4px 15px rgba(0,0,0,0.2)"
                                                        : "0 2px 8px rgba(0,0,0,0.1)",
                                                    "&:hover": {
                                                        background: isParentSelected || openMenus[item.label]
                                                            ? item.gradient
                                                            : "rgba(102, 126, 234, 0.1)",
                                                        transform: minified ? "none" : "translateX(4px)",
                                                    },
                                                    "&:hover .nav-icon-tile": isParentSelected || openMenus[item.label] ? {} : navIconTileHoverSx(item.color),
                                                    transition: "all 0.3s ease",
                                                    opacity: itemGated ? 0.55 : 1,
                                                }}
                                            >
                                                <ListItemIcon sx={{
                                                    minWidth: minified ? 0 : 46,
                                                    justifyContent: minified ? "center" : "flex-start",
                                                }}>
                                                    <Box className="nav-icon-tile" sx={navIconTileSx(item.color, isParentSelected || openMenus[item.label])}>
                                                        <ItemIcon sx={{ fontSize: 20 }} />
                                                    </Box>
                                                </ListItemIcon>
                                                {!minified && (
                                                    <>
                                                        <ListItemText
                                                            primary={item.label}
                                                            primaryTypographyProps={{ fontWeight: 600, fontSize: "14px" }}
                                                        />
                                                        {itemGated ? (
                                                            <Lock sx={{ fontSize: 17, color: "#9ca3af" }} />
                                                        ) : openMenus[item.label] ? (
                                                            <ExpandLess sx={{ color: isParentSelected || openMenus[item.label] ? "#fff" : "#667eea" }} />
                                                        ) : (
                                                            <ExpandMore sx={{ color: isParentSelected || openMenus[item.label] ? "#fff" : "#667eea" }} />
                                                        )}
                                                    </>
                                                )}
                                            </ListItemButton>
                                        </Tooltip>
                                        {!minified && (
                                            <Collapse in={openMenus[item.label]} timeout="auto" unmountOnExit>
                                                <List component="div" disablePadding sx={{ ml: 2 }}>
                                                    {item.children.map((child, childIndex) => {
                                                        const ChildIcon = child.icon;
                                                        const isChildSelected = location.pathname === child.path;
                                                        return (
                                                            <Fade in timeout={200 + childIndex * 50} key={child.label}>
                                                                <ListItemButton
                                                                    onClick={() => leafClick(child.path)}
                                                                    sx={{
                                                                        borderRadius: "8px",
                                                                        mb: 0.5,
                                                                        ml: 1,
                                                                        background: isChildSelected
                                                                            ? `linear-gradient(135deg, ${child.color}20, ${child.color}10)`
                                                                            : "transparent",
                                                                        borderLeft: isChildSelected ? `3px solid ${child.color}` : "3px solid transparent",
                                                                        "&:hover": {
                                                                            background: `linear-gradient(135deg, ${child.color}15, ${child.color}05)`,
                                                                            transform: "translateX(2px)",
                                                                        },
                                                                    }}
                                                                >
                                                                    <ListItemIcon sx={{ minWidth: 38 }}>
                                                                        <Box sx={navIconTileSx(child.color || item.color, false, 26)}>
                                                                            <ChildIcon sx={{ fontSize: 16 }} />
                                                                        </Box>
                                                                    </ListItemIcon>
                                                                    <ListItemText
                                                                        primary={child.label}
                                                                        primaryTypographyProps={{
                                                                            fontWeight: 700,
                                                                            fontSize: "13px",
                                                                            color: isChildSelected ? child.color : "#1f2937",
                                                                        }}
                                                                    />
                                                                </ListItemButton>
                                                            </Fade>
                                                        );
                                                    })}
                                                </List>
                                            </Collapse>
                                        )}
                                    </>
                                ) : (
                                    <Tooltip
                                        title={minified ? item.label : ""}
                                        placement="right"
                                        arrow
                                    >
                                        <ListItemButton
                                            data-tour={item.label}
                                            onClick={() => leafClick(item.path)}
                                            sx={{
                                                borderRadius: "12px",
                                                mb: 1,
                                                justifyContent: minified ? "center" : "flex-start",
                                                px: minified ? 1 : 2,
                                                background: isSelected ? item.gradient : "rgba(255,255,255,0.7)",
                                                color: isSelected ? "white" : "#111827",
                                                boxShadow: isSelected ? "0 4px 15px rgba(0,0,0,0.2)" : "0 2px 8px rgba(0,0,0,0.1)",
                                                "&:hover": {
                                                    background: isSelected ? item.gradient : "rgba(102, 126, 234, 0.1)",
                                                    transform: minified ? "none" : "translateX(4px)",
                                                },
                                                "&:hover .nav-icon-tile": isSelected ? {} : navIconTileHoverSx(item.color),
                                                transition: "all 0.3s ease",
                                                opacity: itemGated ? 0.55 : 1,
                                            }}
                                        >
                                            <ListItemIcon sx={{
                                                minWidth: minified ? 0 : 46,
                                                justifyContent: minified ? "center" : "flex-start",
                                            }}>
                                              <Box className="nav-icon-tile" sx={navIconTileSx(item.color, isSelected)}>
                                                {item.label === "Announcements" || item.label === "Notifications" || item.label === "Messages" ? (
                                                    <Badge
                                                        badgeContent={item.label === "Announcements" ? announcementCount : item.label === "Messages" ? messageCount : notificationCount}
                                                        color="error"
                                                        max={99}
                                                        invisible={
                                                            (item.label === "Announcements" ? announcementCount : item.label === "Messages" ? messageCount : notificationCount) === 0
                                                            || location.pathname.startsWith(item.path)
                                                        }
                                                    >
                                                        <ItemIcon sx={{ fontSize: 20 }} />
                                                    </Badge>
                                                ) : (
                                                    <ItemIcon sx={{ fontSize: 20 }} />
                                                )}
                                              </Box>
                                            </ListItemIcon>
                                            {!minified && (
                                                <>
                                                    <ListItemText
                                                        primary={item.label}
                                                        primaryTypographyProps={{ fontWeight: 600, fontSize: "14px" }}
                                                    />
                                                    {itemGated ? (
                                                        <Lock sx={{ fontSize: 17, color: "#9ca3af" }} />
                                                    ) : null}
                                                </>
                                            )}
                                        </ListItemButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Fade>
                    );
                })}
            </List>

            {!minified ? (
                <Box sx={{ mt: "auto", p: 2 }}>
                    <Box
                        sx={{
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            borderRadius: "12px",
                            p: 2,
                            textAlign: "center",
                            color: "white",
                        }}
                    >
                        <TrendingUp sx={{ fontSize: 24, mb: 1 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, display: "block" }}>
                            Keep Learning!
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            Your progress matters
                        </Typography>
                    </Box>
                </Box>
            ) : null}
        </>
    );

    return (
        <Box sx={{
            display: "flex",
            flexDirection: "column",
            height: "100vh",
            "@supports (height: 100dvh)": { height: "100dvh" },
            overflow: "hidden",
        }}>
            <CssBaseline />

            {/* Show fancy loader when loading */}
            {loading && (
                <LinearProgress
                    sx={{
                        height: 6,
                        background: 'rgba(102, 126, 234, 0.1)',
                        '& .MuiLinearProgress-bar': {
                            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
                        }
                    }}
                />
            )}

            {/* Fancy Top App Bar */}
            <AppBar
                position="fixed"
                sx={{
                    zIndex: (t) => t.zIndex.drawer + 1,
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    boxShadow: "0 4px 20px rgba(102, 126, 234, 0.3)",
                    backdropFilter: "blur(10px)",
                    top: loading ? "6px" : 0,
                    transition: "all 0.3s ease",
                }}
            >
                <Toolbar sx={{
                    justifyContent: "space-between",
                    minHeight: { xs: 56, sm: 70 },
                    height: { xs: 56, sm: 70 },
                    px: { xs: 1.5, sm: 3 },
                }}>
                    {/* Left Side - Toggle Button, Logo & Title */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, minWidth: 0, flex: 1 }}>
                        {/* Sidebar Toggle Button */}
                        <Tooltip title={isMobile ? (mobileFull ? "Close menu" : "Open menu") : (sidebarOpen ? "Collapse sidebar" : "Expand sidebar")} arrow>
                            <IconButton
                                onClick={handleHamburger}
                                sx={{
                                    background: "rgba(255,255,255,0.15)",
                                    color: "#fff",
                                    borderRadius: "12px",
                                    backdropFilter: "blur(10px)",
                                    border: "1px solid rgba(255,255,255,0.2)",
                                    "&:hover": {
                                        background: "rgba(255,255,255,0.25)",
                                        transform: "scale(1.05)",
                                        boxShadow: "0 6px 20px rgba(0,0,0,0.3)"
                                    },
                                    transition: "all 0.3s ease"
                                }}
                            >
                                <MenuIcon sx={{ fontSize: 22 }} />
                            </IconButton>
                        </Tooltip>

                        <Box sx={{
                            background: "rgba(255,255,255,0.15)",
                            borderRadius: "12px",
                            p: { xs: 1, sm: 1.5 },
                            display: { xs: 'none', sm: 'flex' },
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: "0 4px 20px rgba(0,0,0,0.1)"
                        }}>
                            <Typography sx={{ fontSize: '24px' }}>🎓</Typography>
                        </Box>
                        {/* No room for the title on phones; the drawer header names the portal. */}
                        <Box sx={{ minWidth: 0, display: { xs: 'none', sm: 'block' } }}>
                            <Typography
                                variant="h6"
                                noWrap
                                sx={{
                                    color: "#fff",
                                    fontWeight: 700,
                                    fontSize: { xs: '1rem', sm: '1.375rem' },
                                    textShadow: "0 2px 4px rgba(0,0,0,0.3)"
                                }}
                            >
                                {CapitalizeFirstLetter(name)} Portal
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: { xs: 'none', sm: 'block' } }}>
                                Learning Management System
                            </Typography>
                        </Box>
                        {/* Phones: who is signed in, in the title's place. Shrinks to "…" before
                            it can push the toggles off screen. */}
                        <Box
                            onClick={() => navigate(`/${name}/profile`)}
                            aria-label="View student profile"
                            sx={{
                                display: { xs: 'flex', sm: 'none' },
                                alignItems: 'center',
                                gap: 0.75,
                                minWidth: 0,
                                cursor: 'pointer',
                            }}
                        >
                            <Avatar
                                src={userData?.profile_image_url || getPredefinedAvatarImageUrl(userData?.profile_image) || undefined}
                                sx={{ width: 28, height: 28, fontSize: '12px', fontWeight: 700, bgcolor: 'rgba(255,255,255,0.3)', flexShrink: 0 }}
                            >
                                {userData?.full_name?.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography noWrap sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', minWidth: 0 }}>
                                {userData?.first_name || userData?.full_name?.split(" ")[0] || 'Student'}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Right Side - Portal toggle (Student | Parent), User Profile & Logout */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 2 }, flexShrink: 0 }}>
                        <PortalToggleSwitch
                            value="student"
                            onSelectStudent={() => {}}
                            onSelectParent={handleOpenParentPinDialog}
                            size={isCompactAppBar ? "small" : "medium"}
                        />
                        {/* Course + plan students: switch sides from any page. */}
                        {canToggleDashboard && (
                            <DashboardViewToggle
                                value={dashboardView}
                                onChange={setDashboardView}
                                compact={isCompactAppBar}
                            />
                        )}
                        {/* On phones Practice lives in the drawer instead (renderSidebarContent). */}
                        {practiceModeVisible && !isCompactAppBar && (
                        <Tooltip title={PRACTICE_MODE_TOOLTIP} arrow>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.5, borderRadius: "12px", background: "rgba(255,255,255,0.12)", cursor: 'default' }}>
                                <Typography variant="caption" sx={{ color: "#fff", fontWeight: 700 }}>
                                    Practice
                                </Typography>
                                <Switch
                                    size="small"
                                    checked={mockExamsMode === "testing"}
                                    onChange={handlePracticeToggle}
                                    disabled={settingMode}
                                />
                            </Box>
                        </Tooltip>
                        )}
                        <Tooltip title="View student profile" arrow>
                            <Box
                                onClick={() => navigate(`/${name}/profile`)}
                                sx={{
                                    display: { xs: 'none', sm: 'flex' },
                                    alignItems: 'center',
                                    gap: 1.5,
                                    cursor: 'pointer',
                                    borderRadius: 2,
                                    px: 1,
                                    py: 0.5,
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                                }}
                            >
                                <Box sx={{
                                    background: "rgba(255,255,255,0.15)",
                                    borderRadius: "50%",
                                    p: 1
                                }}>
                                    {userData?.profile_image_url ? (
                                        <Avatar src={userData.profile_image_url} sx={{ width: 32, height: 32 }} />
                                    ) : getPredefinedAvatarImageUrl(userData?.profile_image) ? (
                                        <Avatar src={getPredefinedAvatarImageUrl(userData.profile_image)} sx={{ width: 32, height: 32 }} />
                                    ) : userData?.full_name ? (
                                        <Avatar sx={{
                                            width: 32,
                                            height: 32,
                                            fontSize: '14px',
                                            fontWeight: 700,
                                            bgcolor: 'rgba(255,255,255,0.3)'
                                        }}>
                                            {userData.full_name.charAt(0).toUpperCase()}
                                        </Avatar>
                                    ) : (
                                        <AccountCircle sx={{ color: "#fff", fontSize: 28 }} />
                                    )}
                                </Box>
                                {/* Long names and emails end in "…" rather than widening the bar. */}
                                <Box sx={{ textAlign: 'right', minWidth: 0, maxWidth: { sm: 160, md: 240 } }}>
                                    <Typography variant="body2" noWrap sx={{ color: '#fff', fontWeight: 600, fontSize: '14px' }}>
                                        {userData?.full_name || 'Student Portal'}
                                    </Typography>
                                    <Typography variant="caption" noWrap sx={{ display: 'block', color: 'rgba(255,255,255,0.8)', fontSize: '11px' }}>
                                        {userData?.email || 'Welcome back!'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Tooltip>

                        {/* Phones reach the profile from the drawer header, which already shows the avatar. */}
                        <Tooltip title="Logout" arrow>
                            <IconButton
                                onClick={handleLogout}
                                sx={{
                                    background: "rgba(255,255,255,0.15)",
                                    color: "#fff",
                                    borderRadius: "12px",
                                    backdropFilter: "blur(10px)",
                                    border: "1px solid rgba(255,255,255,0.2)",
                                    "&:hover": {
                                        background: "rgba(255,255,255,0.25)",
                                        transform: "translateY(-1px)",
                                        boxShadow: "0 6px 20px rgba(0,0,0,0.3)"
                                    },
                                    transition: "all 0.3s ease"
                                }}
                            >
                                <Logout sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <TesterPortalSwitcher />
                </Toolbar>
            </AppBar>

            {/* PIN dialog for switching to parent portal */}
            <Dialog open={pinDialogOpen} onClose={handleCloseParentPinDialog} maxWidth="xs" fullWidth>
                <DialogTitle>Switch to Parent Portal</DialogTitle>
                <DialogContent sx={{ pt: 2, overflow: "visible" }}>
                    <Typography component="label" variant="body2" sx={{ display: "block", mb: 1.5, fontWeight: 500 }}>
                        PIN
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "center", mb: 1 }}>
                        {[0, 1, 2, 3].map((i) => (
                            <TextField
                                key={i}
                                inputRef={(el) => { pinInputRefs.current[i] = el; }}
                                type="password"
                                inputProps={{
                                    maxLength: 1,
                                    inputMode: "numeric",
                                    pattern: "[0-9]*",
                                    "aria-label": `Digit ${i + 1}`,
                                }}
                                value={switchPin[i] ?? ""}
                                onChange={(e) => {
                                    const v = e.target.value.replace(/\D/g, "").slice(-1);
                                    const next = (switchPin.slice(0, i) + v + switchPin.slice(i + 1)).slice(0, 4);
                                    setSwitchPin(next);
                                    if (v && i < 3) setTimeout(() => pinInputRefs.current[i + 1]?.focus(), 0);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && switchPin.length === 4) handleSwitchToParent();
                                    if (e.key === "Backspace" && !switchPin[i] && i > 0) {
                                        pinInputRefs.current[i - 1]?.focus();
                                    }
                                }}
                                error={!!switchPinError}
                                disabled={switchPinLoading}
                                sx={{ width: 56, "& .MuiInputBase-input": { textAlign: "center", fontSize: "1.25rem" } }}
                            />
                        ))}
                    </Box>
                    {switchPinError && (
                        <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                            {switchPinError}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
                    <Button
                        variant="text"
                        size="small"
                        onClick={handleForgotPin}
                        disabled={forgotPinLoading || switchPinLoading}
                        sx={{ textTransform: "none", color: "text.secondary" }}
                    >
                        {forgotPinLoading ? "Resetting..." : "Forgot PIN?"}
                    </Button>
                    <Box>
                        <Button onClick={handleCloseParentPinDialog} disabled={switchPinLoading || forgotPinLoading} sx={{ mr: 1 }}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSwitchToParent}
                            disabled={switchPinLoading || forgotPinLoading || switchPin.length !== 4}
                        >
                            {switchPinLoading ? "Switching…" : "Switch to Parent"}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            <Box sx={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden", width: "100%" }}>
            {!isMobile && (
                <Drawer
                    variant="permanent"
                    sx={{
                        width: desktopDrawerWidth,
                        flexShrink: 0,
                        alignSelf: "stretch",
                        minHeight: 0,
                        transition: "width 0.3s ease",
                        [`& .MuiDrawer-paper`]: drawerPaperSx(
                            desktopDrawerWidth,
                            appBarHeight,
                            "4px 0 20px rgba(0,0,0,0.1)"
                        ),
                    }}
                >
                    {renderSidebarContent(!sidebarOpen)}
                </Drawer>
            )}
            {/* Icon rail on tablets only — on phones its 64px is a fifth of the screen,
                so the hamburger opens the full drawer instead. */}
            {isMobile && !isCompactAppBar && (
                <Drawer
                    variant="permanent"
                    sx={{
                        width: MINI_DRAWER_WIDTH,
                        flexShrink: 0,
                        alignSelf: "stretch",
                        minHeight: 0,
                        [`& .MuiDrawer-paper`]: drawerPaperSx(
                            MINI_DRAWER_WIDTH,
                            appBarHeight,
                            "2px 0 8px rgba(0,0,0,0.08)"
                        ),
                    }}
                >
                    {renderSidebarContent(true)}
                </Drawer>
            )}
            {isMobile && (
                <Drawer
                    variant="temporary"
                    open={mobileFull}
                    onClose={() => setMobileFull(false)}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        [`& .MuiDrawer-paper`]: {
                            ...drawerPaperSx(
                                drawerWidth,
                                appBarHeight,
                                "4px 0 20px rgba(0,0,0,0.15)"
                            ),
                            width: `min(${drawerWidth}px, 85vw)`,
                        },
                    }}
                >
                    {renderSidebarContent(false)}
                </Drawer>
            )}

            {/* Enhanced Main Content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    minHeight: 0,
                    minWidth: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                    p: { xs: 1, sm: 2, md: 3 },
                    mt: `${appBarHeight}px`,
                    // Custom scrollbar for main content
                    "&::-webkit-scrollbar": {
                        width: "10px",
                    },
                    "&::-webkit-scrollbar-track": {
                        background: "rgba(0,0,0,0.05)",
                        borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        background: "linear-gradient(180deg, #667eea 0%, #764ba2 100%)",
                        borderRadius: "10px",
                        border: "2px solid #f9f9f9",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                        background: "linear-gradient(180deg, #3B2A9F 0%, #D62926 100%)",
                    },
                }}
            >
                <Fade in timeout={500}>
                    <Box>
                        {/* An ended trial leaves only the plan screen. A lapsed paid plan
                            can still open the catalogues. */}
                        {planFullyLocked && !onLapsedAllowedPage ? (
                            <Navigate to={`/${name}/choose-plan`} replace />
                        ) : (
                            <Outlet key={dashboardView} context={{ openParentSwitch: handleOpenParentPinDialog }} />
                        )}
                    </Box>
                </Fade>
            </Box>
            <AssistantDrawer role="student" />
            <ParentWelcomeTour
                portal="student"
                onRequestDrawer={isCompactAppBar ? setMobileFull : undefined}
                userId={userData?.id}
                firstName={userData?.first_name || userData?.full_name?.split(" ")[0]}
            />
            </Box>
        </Box>
    );
};

export default StudentLayout;
